interface Config {
  primaryAuthorizationServer: {
    baseUrl: string;
    id: string;
  };
  oauthClientId: string;
  oktaURL: string;
  amplitudeApiKey: string;
  logoUrl: string | undefined;
  unifiedPatientLoginEnabled: boolean;
  redirectUri: string;
  scopes: string[];
  api: {
    timeout: number;
    retries: number;
  };
  iam: {
    identityServiceUrl: string;
  };
}

const defaultConfigs = {
  scopes: [
    "openid",
    "profile",
    "email",
    "athena/user/Identity.Account.Profile.write",
    "athena/user/Identity.Account.Groups.read",
    "athena/user/Identity.Account.Profile.read",
    "athena/user/Identity.Account.Factors.read",
    "athena/user/Identity.Account.Factors.write",
  ],
  redirectUri: "authz_callback",
  logoUrl: process.env.REACT_APP_LOGO_URL,
  // Enabled per environment below; one build serves every environment, so not an env var.
  unifiedPatientLoginEnabled: false,
  api: {
    timeout: 30000,
    retries: 2,
  },
};

const nonProdAmplitudeApiKey = "20dc2a80bf07774d9fc999f614e9ea5e";
const preProdAmplitudeApiKey = "e83bc020b299658e1e1c454fc9616d92";
const prodAmplitudeApiKey = "c41bf44737b4598dcae4c0eaec9b5833";

const defaultOktaUrl = process.env.REACT_APP_OKTA_URL ?? 'https://identity.int.athenahealth.com/';
const defaultAuthServerBaseUrl = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? 'https://myidentity-int.aws.athenahealth.com/oauth2/';  //'https://login.dev.platform.athenahealth.com:3000/oauth2/'

// point to int for local dev
const localDevConfig: Config = {
  primaryAuthorizationServer: {
    baseUrl: defaultAuthServerBaseUrl,
    id: process.env.REACT_APP_OKTA_AUTHSERVER_ID ?? "ausc16vzbfqYFn4jP0h7",
  },
  oauthClientId: process.env.REACT_APP_OAUTH_CLIENT_ID ?? "0oa24wvh20dXOKUTb0h8",
  oktaURL: defaultOktaUrl,
  amplitudeApiKey: nonProdAmplitudeApiKey,
  iam: {
    identityServiceUrl: process.env.REACT_APP_IAM_CORE_IDENTITY_SERVICE_URL
      ? process.env.REACT_APP_IAM_CORE_IDENTITY_SERVICE_URL + "/api/v1"
      : "https://identity.int.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
  // Must follow the spread above, which would otherwise reset it to the default.
  unifiedPatientLoginEnabled: true,
};

const devConfig: Config = {
  primaryAuthorizationServer: {
    baseUrl: "https://myidentity-dev.aws.athenahealth.com/oauth2/",
    id: "aus13kbtj4HAdI3451t7",
  },
  oauthClientId: "0oa10jmz9nyEM2HoR1t8",
  oktaURL: "https://identity.dev.athenahealth.com/",
  amplitudeApiKey: nonProdAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.dev.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
  unifiedPatientLoginEnabled: true,
};

const intConfig: Config = {
  primaryAuthorizationServer: {
    baseUrl: "https://myidentity-int.aws.athenahealth.com/oauth2/",
    id: "ausc16vzbfqYFn4jP0h7",
  },
  oauthClientId: "0oa24wvh20dXOKUTb0h8",
  oktaURL: "https://identity.int.athenahealth.com/",
  amplitudeApiKey: nonProdAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.int.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
  unifiedPatientLoginEnabled: true,
};

const preprodConfig: Config = {
  primaryAuthorizationServer: {
    baseUrl: "https://myidentity-preprod.aws.athenahealth.com/oauth2/",
    id: "ausv45j1o7FKYZbu1297",
  },
  oauthClientId: "0oaw5ijqpbB1AkEHn297",
  oktaURL: "https://identity.preprod.athenahealth.com/",
  amplitudeApiKey: preProdAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.preprod.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
};

const prodPrimaryAuthServer = {
  baseUrl: "https://myidentity.platform.athenahealth.com/oauth2/",
  id: "auset0ja9xZ2Hniep296",
};

const prodConfig = {
  primaryAuthorizationServer: prodPrimaryAuthServer,
  oauthClientId: "0oar6go3edRUglvkD297",
  oktaURL: "https://identity.athenahealth.com/",
  amplitudeApiKey: prodAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
};

const supportPhiConfig = {
  primaryAuthorizationServer: {
    baseUrl: "https://myidentity-support.platform.athenahealth.com/oauth2/",
    id: "ausu5cwvrZnWYasjS296",
  },
  oauthClientId: "0oarf18q39wbA4VhK297",
  oktaURL: "https://identity.athenahealth.com/",
  amplitudeApiKey: prodAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.preview.api.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
};

const supportNonPhiConfig = {
  primaryAuthorizationServer: {
    baseUrl: "https://myidentity-support-demo.platform.athenahealth.com/oauth2/",
    id: "aus1eumlr5MD3rYcn297",
  },
  oauthClientId: "0oarf1cw47OHBe4pG297",
  oktaURL: "https://identity.athenahealth.com/",
  amplitudeApiKey: prodAmplitudeApiKey,
  iam: {
    identityServiceUrl: "https://identity.salesdemo.api.athena.io/identity/api/v1",
  },
  ...defaultConfigs,
};

const envConfigMappings: { [key: string]: Config } = {
  "http://localhost:33000": localDevConfig,
  "https://lwa-user-profile-management.athenahealth.com:33000": localDevConfig,
  "https://myidentity-dev-profile.platform.athenahealth.com": devConfig,
  "https://myidentity-int-profile.platform.athenahealth.com": intConfig,
  "https://myidentity-preprod-profile.platform.athenahealth.com": preprodConfig,
  "https://myidentity-profile.platform.athenahealth.com": prodConfig,
  "https://myidentity-support-profile.platform.athenahealth.com":
    supportPhiConfig,
  "https://myidentity-support-demo-profile.platform.athenahealth.com":
    supportNonPhiConfig,
};

export const envConfig = Object.keys(envConfigMappings).reduce(
  (config, key) => {
    config[key] = { ...defaultConfigs, ...envConfigMappings[key] };
    return config;
  },
  {} as { [key: string]: Config },
);

const config = envConfig[window.location.origin] || localDevConfig;
export default config;
